/**
 * A page in the PUBLIC route tree that still needs a session: /onboarding.
 *
 * The wizard lives in the public tree so the app shell (sidebar, onboarding
 * gate) does not wrap it, which left it guarding itself with a bare
 * `isAuthenticated ? <Onboarding /> : <Navigate to="/login" />`. That lost two
 * things:
 *  - the destination. The Shopify callback sends a merchant still in setup to
 *    /onboarding?platform=shopify&success=true&shop=…; logged out, they signed
 *    in and landed on /business with the wizard step and the store gone.
 *  - the difference between "logged out" and "not known yet". App's session
 *    check starts at null and the public tree does not wait for it, so a null
 *    took the Navigate branch before the check had answered.
 * Now it waits while the session resolves and carries the destination the same
 * way the authenticated tree does (loginPathFor).
 */
import type { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { PageLoader } from './PageLoader';
import { loginPathFor } from './loginRedirect';

interface Props {
  /** App's session state; null while it is still being resolved. */
  isAuthenticated: boolean | null;
  children: ReactElement;
}

export function RequireSession({ isAuthenticated, children }: Props): ReactElement {
  const location = useLocation();
  if (isAuthenticated === false) {
    return <Navigate to={loginPathFor(location.pathname, location.search)} replace />;
  }
  // null is "not answered yet", never "logged out".
  return isAuthenticated ? children : <PageLoader />;
}
